import {LocalVideoOptionsArray, BackgroundVideoOptionsArray} from '../../../types';
import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import {MouseHijack, MouseHijackCursorOptions, WithCaption, WithCaptionProps} from '../../wraps';
import {RatioKeptVideo} from './RatioKeptVideo';
import {Typography} from '../typography';
import Grid from '@material-ui/core/Grid';
import {useBreakpointUp, AccentColorOptions} from 'epic-ue-theme';
import {VerticalSpacer} from '../spacer';
import {CenteredGrid, GridContainer} from '../grid';
import {CenteredTextBlock} from '../text';
import {htmlVideoInMediaOptions, isValidMediaOptions} from '../../../utils';
import {VideoLightbox} from '../lightbox';

const Styled = styled.div`
    padding: 8px 0;

    .video-caption {
        text-align: right;

        h4,
        .paragraph {
            display: inline-block;
        }

        h4 {
            margin-right: 8px;
        }
    }

    .video-wrap {
        background-color: #000;

        .video-wrapper {
            background-color: #000;
        }
    }
`;

export interface EmbeddedVideoProps {
    mainContentOptions?: LocalVideoOptionsArray;
    mediaOptions?: BackgroundVideoOptionsArray;
    placeholderImageCaption?: WithCaptionProps;
    label?: string;
    caption?: string;
    accentColor?: AccentColorOptions;
    title?: string;
    desc?: string;
    useIcons?: boolean;
    noGridSides?: boolean;
    mute?: boolean;
    placeholderAutoplay?: boolean;
    mouseHijackCursorOptions?: MouseHijackCursorOptions | 'none';
    openLightbox?: boolean;
}

export const EmbeddedVideo = ({
    accentColor,
    mainContentOptions,
    mediaOptions,
    placeholderImageCaption,
    label,
    caption,
    title,
    desc,
    useIcons = false,
    noGridSides,
    mute = true,
    openLightbox,
    placeholderAutoplay,
    mouseHijackCursorOptions = 'play'
}: EmbeddedVideoProps): JSX.Element | null => {
    const lgUp = useBreakpointUp('lg');
    const [activated, setActivated] = useState(false);

    const toggleLightbox = useCallback(() => {
        setActivated(!activated);
    }, [setActivated, activated]);

    const play = useCallback(() => {
        setActivated(true);
    }, [setActivated]);
    const handleKeyUp = useCallback(
        (e) => {
            if (e.key === 'Enter' || e.key === 'Space' || e.key === ' ') {
                setActivated(true);
            }
        },
        [setActivated]
    );

    if (!isValidMediaOptions(mediaOptions)) {
        return null;
    }

    const previewContent =
        !mainContentOptions ||
        !mainContentOptions.length ||
        !isValidMediaOptions(mainContentOptions)
            ? htmlVideoInMediaOptions(mediaOptions)
            : mainContentOptions;

    if (!previewContent || !previewContent.length) {
        return null;
    }

    const preview = (
        <WithCaption {...mediaOptions?.[0]?.htmlVideo?.videoCaption}>
            <RatioKeptVideo
                key="embed-video-placeholder"
                mediaOptions={previewContent}
                mute={mute}
                autoplay={placeholderAutoplay}
                controls={placeholderAutoplay}
            />
        </WithCaption>
    );

    return (
        <Styled className="embedded-video">
            <GridContainer>
                <CenteredGrid>
                    {!noGridSides && lgUp && <Grid item lg={1} />}
                    <Grid
                        item
                        xs={12}
                        lg={!noGridSides ? 10 : 12}
                        onKeyUp={handleKeyUp}
                        style={{padding: 0}}>
                        <CenteredTextBlock headline={title} description={desc} />
                        {title || desc ? (
                            <VerticalSpacer variant="custom" spacing={6} downSmSpacing={4} />
                        ) : (
                            ''
                        )}

                        {activated ? (
                            openLightbox ? (
                                <VideoLightbox
                                    mediaOptions={mediaOptions}
                                    isOpen={activated}
                                    toggleLightbox={toggleLightbox}
                                    accentColor={accentColor}
                                />
                            ) : (
                                <WithCaption {...mediaOptions?.[0]?.htmlVideo?.videoCaption}>
                                    <RatioKeptVideo
                                        key="embed-video-activated"
                                        mediaOptions={mediaOptions}
                                        placeholderImageCaption={placeholderImageCaption}
                                        mute={mute}
                                        forcePlay={true}
                                    />
                                </WithCaption>
                            )
                        ) : mouseHijackCursorOptions === 'none' ? (
                            preview
                        ) : (
                            <MouseHijack
                                onClick={play}
                                hoverCursorType={mouseHijackCursorOptions}
                                useIcons={useIcons}
                                accentColor={accentColor}
                                forceOverlay={true}>
                                {preview}
                            </MouseHijack>
                        )}

                        <div className="video-caption">
                            <VerticalSpacer variant="custom" spacing={1} />
                            {label && <Typography variant="h4">{label}</Typography>}
                            {caption && (
                                <Typography variant="p" caption>
                                    {caption}
                                </Typography>
                            )}
                        </div>
                    </Grid>
                    {!noGridSides && lgUp && <Grid item lg={1} />}
                </CenteredGrid>
            </GridContainer>
        </Styled>
    );
};
